import type { PartialDeep } from 'type-fest';

import type { RequiredPermissions } from 'types/Permissions';

import { builderPermissions } from 'components/sections/createModify/builderPermissions';
import { BuilderType } from 'enums/builderType';
import { FeatureBundleSet } from 'enums/featureBundle';
import { RoutePath } from 'enums/routePath';

import type { FeatureBundle } from 'store/client/graphql';
import { AccessLevel, GuaranteedOfferPackage, ResourceType } from 'store/client/graphql';

import { doUserRooftopsHaveFeatureEnabled } from './featureBundleRooftopUtils';

const ROUTE_TO_FEATURE_MAPPING = {
  [RoutePath.APPOINTMENTS]: FeatureBundleSet.APPOINTMENT,
  [RoutePath.CONVERSATIONS]: FeatureBundleSet.CONVERSATION,
  [RoutePath.LEADS]: FeatureBundleSet.LEAD,
  [RoutePath.RETAIL_ITEMS]: FeatureBundleSet.RETAIL,
  [RoutePath.TASKS]: FeatureBundleSet.TASK,
  [RoutePath.TRADE_IN_ITEMS]: FeatureBundleSet.TRADE_IN,
} as const;

interface isFeatureBuilderEnabledForUserParameters {
  builderType: BuilderType;
  feature: FeatureBundleSet;
  hasPermissions: (permissions: RequiredPermissions) => boolean;
  userRooftops?: null | Record<'bundle', PartialDeep<FeatureBundle>>[];
}
export const isFeatureBuilderEnabledForUser = ({
  builderType,
  feature,
  hasPermissions,
  userRooftops,
}: isFeatureBuilderEnabledForUserParameters): boolean =>
  doUserRooftopsHaveFeatureEnabled(feature, userRooftops) && hasPermissions(builderPermissions[builderType]);

export const isFeatureRouteEnabledForUser = (
  path: string,
  userRooftops?: null | Record<'bundle', PartialDeep<FeatureBundle>>[]
): boolean => {
  const matchedRoute = Object.keys(ROUTE_TO_FEATURE_MAPPING).find(key => path.startsWith(key));

  if (!matchedRoute) {
    return true;
  }

  return doUserRooftopsHaveFeatureEnabled(ROUTE_TO_FEATURE_MAPPING[matchedRoute], userRooftops);
};

/**
 * Check to see if a rooftop's Easy Offer package gives access to trade-in valuation adjustments.
 * Unlike the Easy Offer settings page itself (available to ESSENTIAL as well), valuation adjustments
 * are only included in the INSIGHT package — PRO cannot create adjustments, so surfacing the tab there
 * would only lead to a dead end.
 *
 * @example isTradeInValuationAdjustmentsEnabled(rooftop?.bundle?.features?.tradeIn?.guaranteedOfferPackage)
 * @returns True if the package includes trade-in valuation adjustments
 */
export const isTradeInValuationAdjustmentsEnabled = (
  guaranteedOfferPackage: GuaranteedOfferPackage | null | undefined
): boolean => guaranteedOfferPackage === GuaranteedOfferPackage.INSIGHT;

export const canUserEditFeatureBundles = (hasPermissions: (permissions: RequiredPermissions) => boolean): boolean =>
  hasPermissions([{ level: AccessLevel.FULL, resource: ResourceType.FEATURE_BUNDLES }]) &&
  hasPermissions(builderPermissions[BuilderType.ROOFTOPS_MODIFY]);
